import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { CharacterCounter } from '@/components/common/CharacterCounter';
import { OptionSelect } from '@/components/common/OptionSelect';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  PCMP_PROJECT_CODE_MAX_LENGTH,
  PCMP_PROJECT_DESCRIPTION_MAX_LENGTH,
  PCMP_PROJECT_NAME_MAX_LENGTH,
} from '../../constants/pcmpProject.constants';
import type { PcmpProjectFormValues } from '../../types/pcmpProjectForm.schema';
import type { PcmpProjectOptions } from '../../types/pcmpProject.types';
import { PcmpFormSection } from './PcmpFormSection';

interface PcmpProjectDetailsSectionProps {
  options: PcmpProjectOptions;
}

const Required = () => <span className="text-destructive">*</span>;

export function PcmpProjectDetailsSection({ options }: PcmpProjectDetailsSectionProps) {
  const { control } = useFormContext<PcmpProjectFormValues>();
  const editionOptions = useMemo(
    () => options.bookEditions.map((edition) => ({ id: edition.no, name: edition.name })),
    [options.bookEditions],
  );

  return (
    <PcmpFormSection id="pcmp-project-details-heading" title="Project">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FormField
          control={control}
          name="name"
          render={({ field, fieldState }) => (
            <FormItem className="sm:col-span-2">
              <FormLabel>
                Name <Required />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., Primary Project-2027"
                  className="h-10"
                  maxLength={PCMP_PROJECT_NAME_MAX_LENGTH}
                  aria-required="true"
                  aria-invalid={fieldState.invalid || undefined}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="code"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>
                Code <Required />
              </FormLabel>
              <FormControl>
                <Input
                  {...field}
                  placeholder="e.g., PP27"
                  className="h-10 font-mono"
                  maxLength={PCMP_PROJECT_CODE_MAX_LENGTH}
                  autoComplete="off"
                  aria-required="true"
                  aria-invalid={fieldState.invalid || undefined}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <FormField
          control={control}
          name="companyId"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel htmlFor="pcmp-project-company-field">
                Company <Required />
              </FormLabel>
              <OptionSelect
                id="pcmp-project-company-field"
                options={options.companies}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select company"
                className="h-10"
                aria-invalid={fieldState.invalid}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="bookEditionNo"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel htmlFor="pcmp-project-edition-field">
                Book Edition <Required />
              </FormLabel>
              <OptionSelect
                id="pcmp-project-edition-field"
                options={editionOptions}
                value={field.value}
                onChange={field.onChange}
                placeholder="Select edition"
                className="h-10"
                aria-invalid={fieldState.invalid}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="slNo"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>SL No</FormLabel>
              <FormControl>
                <Input
                  {...field}
                  inputMode="numeric"
                  placeholder="Order in lists"
                  className="h-10"
                  aria-invalid={fieldState.invalid || undefined}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={control}
        name="description"
        render={({ field, fieldState }) => (
          <FormItem>
            <FormLabel>Description</FormLabel>
            <FormControl>
              <Textarea
                {...field}
                rows={2}
                placeholder="Optional notes about this project."
                maxLength={PCMP_PROJECT_DESCRIPTION_MAX_LENGTH}
                aria-invalid={fieldState.invalid || undefined}
              />
            </FormControl>
            <div className="flex items-center justify-between gap-2">
              <FormMessage />
              <CharacterCounter
                current={field.value.length}
                max={PCMP_PROJECT_DESCRIPTION_MAX_LENGTH}
                className="ml-auto"
              />
            </div>
          </FormItem>
        )}
      />
    </PcmpFormSection>
  );
}
